import {useCallback, useEffect, useMemo, useState} from 'react'

type PaginationOptions = {
  defaultOffset?: number
  limit?: number
}

export type PaginationApi = {
  value: {
    offset: number
    limit: number
  }
  change: (nextOffset: number) => void
  reset: () => void
  sanitizeWith: (nextTotal: number) => void
}

/**
 * The goal of this hook is to concentrate pagination related actions in the single API object.
 * This should help devs to drill single prop into children components.
 * It returns useful function "sanitizeWith", that helps to adjust current offset if there's nothing to show
 *
 * @example
 * const paginationApi = usePagination({limit: 5})
 * const {items, totalCount} = useQuery(...)
 *
 * paginationApi.sanitizeWith(totalCount) // it's safe to call in render
 */
export function usePagination(options?: PaginationOptions): PaginationApi {
  const {defaultOffset = 0, limit = 25} = options ?? {}

  const [offset, setOffset] = useState(defaultOffset)

  const [total, setTotal] = useState<number | null>(null)

  const reset = useCallback((): void => setOffset(defaultOffset), [defaultOffset, setOffset])

  const sanitizeWith = useCallback(
    (nextTotal: number) => {
      if (nextTotal !== total) setTotal(nextTotal)
    },
    [total],
  )

  useEffect(() => {
    const isNothingToShow = total != null && offset > 0 && offset >= total

    if (isNothingToShow) setOffset(Math.max(0, total - limit))
  }, [limit, offset, setOffset, total])

  return useMemo(
    () => ({
      value: {offset, limit},
      change: setOffset,
      reset,
      sanitizeWith
    }),
    [limit, offset, reset, setOffset, sanitizeWith],
  )}

